import classNames from "classnames";
import classes from "./SalesCoverageTile.module.less";
import type { SalesCoverage } from "~/FreeCapacityPush/useFCCState";
import { Card } from "~/Configurators/Linear/components/Card";

import { Bar } from "react-chartjs-2";
import {
  C_CHART_1,
  C_CHART_2,
  C_CHART_3,
  C_CHART_4,
  C_CHART_5,
  C_ON_SURFACE,
  C_SURFACE,
} from "~/common/components/icon/Colors";
import { Trans, useTranslation } from "react-i18next";
import { centeredTextPlugin, formatYTicks, useRerenderOnResize } from "~/FreeCapacityPush/graphUtils";
import { isNonNull } from "~/utils/reducers";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { ProductService } from "~/AddToCart/ProductService";
import { InfoCircle } from "~/FreeCapacityPush/components/InfoCircle/InfoCircle";
import { NumberFormatter as NF } from "~/utils/NumberFormatter";
import type { CoverageType, CoverageUnit } from "~/FreeCapacityPush/api/fetchTrainDetails";
import { MenuItem } from "@mui/material";

import { Chart as ChartJS } from "chart.js/auto";

import {
  CategoryScale,
  type ChartData,
  type ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import { TextField } from "~/common/m3/TextField/TextField";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface Props {
  className?: string;
  salesCoverage?: SalesCoverage;
  selectedProductCode?: string;
  productLineLabel?: string;
  salesCoverageType: CoverageType;
  salesCoverageUnit: CoverageUnit;
  onSalesCoverageTypeChange: (coverageType: CoverageType) => void;
  onSalesCoverageUnitChange: (unit: CoverageUnit) => void;
}

const BG_COLORS = [C_CHART_5, C_CHART_4, C_CHART_3, C_CHART_2, C_CHART_1];

export const SalesCoverageTile = ({
  className,
  salesCoverage,
  selectedProductCode,
  productLineLabel,
  salesCoverageType,
  salesCoverageUnit,
  onSalesCoverageTypeChange,
  onSalesCoverageUnitChange,
}: Props) => {
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();

  const salesCoverageValues = salesCoverage?.[salesCoverageType]?.[salesCoverageUnit];

  const productDescriptor = ProductService.useNullableProductDescriptor({
    productCode: selectedProductCode ?? "",
    priceReference: "",
  });

  const data: ChartData<"bar"> = {
    labels: undefined,
    datasets: [
      {
        data: [],
        backgroundColor: BG_COLORS,
        borderRadius: 4,
        borderSkipped: false,
        barThickness: 32,
      },
    ],
  };
  let key = "-";
  if (salesCoverageValues) {
    const { ytdSales = 0, orderbook = 0, gap = 0, plan = 0, budget = 0 } = salesCoverageValues;

    data.labels = [
      t("page.fcc.salesCoverage.ytd"),
      t("page.fcc.salesCoverage.ob"),
      t("page.fcc.salesCoverage.gap"),
      t("page.fcc.salesCoverage.plan"),
    ];
    data.datasets[0].data = [
      [0, ytdSales],
      [ytdSales, ytdSales + orderbook],
      [ytdSales + orderbook, ytdSales + orderbook + gap],
      [0, plan],
    ];
    if (!selectedProductCode && salesCoverageType !== "FCC_CATALOG_ONLY") {
      data.labels.push(t("page.fcc.salesCoverage.budget"));
      data.datasets[0].data.push([0, budget]);
    }
    key = [ytdSales, orderbook, gap, plan, budget].join("-");
  }

  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        displayColors: true,
        callbacks: {
          title: function (context) {
            return "";
          },
          label: function (context) {
            const { start = null, end = null } = context.parsed._custom ?? {};
            if (start !== null && end !== null) {
              const val = end - start;
              return NF.getDefaultFormatter().format(val);
            }
            return "";
          },
        },
        // @ts-ignore - this exists
        labelTextColor: C_ON_SURFACE,
        titleColor: C_ON_SURFACE,
        bodyColor: C_ON_SURFACE,
        backgroundColor: C_SURFACE,
      },
    },
    scales: {
      y: {
        ticks: {
          display: !!salesCoverageValues,
          callback: formatYTicks,
        },
        border: {
          display: !!salesCoverageValues,
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  const accountNumber = window.SCHA.user.selectedAccount?.displayUid ?? "N/A";

  let subtitle;
  if (selectedProductCode) {
    subtitle = productDescriptor?.base.designation;
  } else if (productLineLabel) {
    subtitle = t("page.fcc.salesCoverage.productLine", { name: productLineLabel });
  } else {
    subtitle = t("page.fcc.account") + " " + accountNumber;
  }

  const tooltipContent = (
    <div className={classes.tooltipContent}>
      <Trans i18nKey={"page.fcc.salesCoverage.tooltip"} />
    </div>
  );

  const height = isDesktop ? 186 : 140;
  const [containerRef, chartRef] = useRerenderOnResize();

  return (
    <Card className={classNames(classes.root, className)}>
      <InfoCircle content={tooltipContent} placement={"left"} className={classes.infoIcon} />
      <div className={classes.title}>
        <span>{t("page.fcc.salesCoverage.title")}</span>
      </div>
      <div className={classes.subtitle} title={productLineLabel}>
        {subtitle}
      </div>
      <div className={classes.content}>
        <div className={classes.selects}>
          <TextField
            select
            size={"small"}
            variant={"outlined"}
            value={salesCoverageType}
            onChange={(e) => onSalesCoverageTypeChange(e.target.value as CoverageType)}
          >
            <MenuItem value={"CUSTOMER_CATALOG"}>{t("page.fcc.salesCoverage.option.customerCatalog")}</MenuItem>
            <MenuItem value={"FCC_CATALOG_ONLY"}>{t("page.fcc.salesCoverage.option.fccOnly")}</MenuItem>
          </TextField>
          <TextField
            select
            size={"small"}
            variant={"outlined"}
            value={salesCoverageUnit}
            onChange={(e) => onSalesCoverageUnitChange(e.target.value as CoverageUnit)}
          >
            <MenuItem value={"EURO"}>{t("page.fcc.salesCoverage.option.euro")}</MenuItem>
            <MenuItem value={"PIECES"}>{t("page.fcc.salesCoverage.option.pieces")}</MenuItem>
          </TextField>
        </div>
        <div className={"w-100"} style={{ height: height + "px" }} ref={containerRef}>
          <Bar
            key={key}
            // @ts-ignore
            ref={chartRef}
            options={options}
            data={data}
            height={height}
            plugins={[
              !salesCoverageValues &&
                centeredTextPlugin(
                  t("page.fcc.salesCoverage.noData"),
                  getFontStyle(FontSize.TitleLarge).fontSize,
                  C_ON_SURFACE,
                ),
            ].filter(isNonNull)}
          />
        </div>
      </div>
    </Card>
  );
};
